<template>
  <!-- Sizing comes from the caller's class (PropertyEditorFactory passes its own
       through), so this wrapper never fights the plain-input branch's width. -->
  <div class="relative">
    <BuilderInput
      type="text" class="w-full text-sm pl-2 pr-7 py-1"
      :placeholder="placeholder"
      :modelValue="displayValue"
      @update:modelValue="onTyped" />
    <button
      type="button"
      class="absolute right-0 top-0 h-full px-1.5 rounded-r bg-blue-600 hover:bg-blue-500 text-white transition-colors"
      title="Open String Builder — insert icons and variables"
      @click="builderOpen = true">
      <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-.464 5.535a1 1 0 10-1.415-1.414 3 3 0 01-4.242 0 1 1 0 00-1.415 1.414 5 5 0 007.072 0z" clip-rule="evenodd" />
      </svg>
    </button>

    <IconPicker
      v-model="builderOpen"
      :initialText="builderText"
      :availableTabs="availableTabs"
      :variables="variables"
      :previewValues="previewValues"
      @text-updated="onBuilderApply" />
  </div>
</template>

<script setup>
/**
 * StringBuilderField.vue
 *
 * A free-text field that also opens the Designer's existing String
 * Builder (IconPicker.vue — its dialog header reads "String Builder"),
 * the same one the Properties panel puts behind the blue button next to
 * `text` / `value_str`. Nothing about the builder is reimplemented here:
 * this component only supplies `initialText`, receives the composed
 * string back from its `text-updated` event, and writes it out through
 * `update:modelValue`. Icons, variables, the strftime tab, the cursor
 * insertion behaviour and the Apply/Cancel flow are all IconPicker's.
 *
 * Resolved by PropertyEditorFactory.vue for a free-text editor when the
 * caller asks for the builder (`stringBuilder`), so the widget property's
 * own schema type is what decides whether it appears — see
 * PropertyEditorMeta.js. There is no property-name special case here and
 * no mapping-specific composer: MappingRuleRow gets the same field a
 * constant string value would.
 *
 * Representation is deliberately identical to a normal string property's:
 * `modelValue` carries the stored form (icons as their actual Private Use
 * Area character), and the visible input shows the `\uXXXX` notation the
 * Properties panel shows, via the shared helpers in
 * utils/iconTextNotation.js. So preview, validation, HA generation and
 * YAML serialization all keep the semantics they already had — a mapping
 * result is still a plain string in `rule.result`.
 *
 * `variables` is the complete Variables list for the value being edited,
 * handed to IconPicker's own `variables` prop — which replaces its built-in
 * openHASP runtime placeholders instead of adding to them. That is the whole
 * context separation: the builder itself never learns what a Home Assistant
 * binding is, it just shows the list it was given. The default here is *no*
 * variables, because a string edited in this tree is rendered by a Jinja
 * template, where a plate-side `%ip%` would resolve to nothing.
 *
 * For a mapping result the list holds one entry, the incoming value, shown as
 * "Incoming value" and inserting the readable `{{value}}`; `valueExpression`
 * is what that alias becomes in the model — the generator's own
 * buildIncomingValueExpression() output, since a mapping result is emitted
 * verbatim and only real Jinja runs. See incomingValueToken.js for why the
 * two forms differ and where they are converted.
 */
import { computed, ref } from 'vue';
import { iconCharsToNotation, iconNotationToChars } from '../../../utils/iconTextNotation.js';
import {
  toIncomingValueToken,
  fromIncomingValueToken,
} from '../../../automation/schema/incomingValueToken.js';
import BuilderInput from './BuilderInput.vue';
import IconPicker from '../../IconPicker.vue';

const props = defineProps({
  modelValue:  { type: [String, Number], default: '' },
  placeholder: { type: String, default: '' },
  // Forwarded verbatim to IconPicker — see its own prop docs.
  availableTabs:   { type: Array,  default: () => ['icons', 'variables'] },
  previewValues:   { type: Object, default: () => ({}) },
  // The COMPLETE Variables list for this value, replacing IconPicker's
  // openHASP runtime placeholders rather than extending them. Defaults to
  // none: this field lives in the Home Assistant builder, where a string is
  // rendered by a Jinja template and `%ip%`/`%hostname%` and friends — which
  // only the plate's own text rendering substitutes — would generate nothing.
  variables:       { type: Array,  default: () => [] },
  // The binding's incoming-value Jinja expression, from
  // buildIncomingValueExpression(). Present only where the value has such a
  // context (a mapping result); when empty the alias translation below is a
  // no-op and this behaves as a plain text + icons field.
  valueExpression: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);

const builderOpen = ref(false);

// ── Stored form ↔ editable form ─────────────────────────────────────────────
//
// Two independent, order-free substitutions, each with its own module:
// icons (utils/iconTextNotation.js) and the incoming-value alias
// (automation/schema/incomingValueToken.js). The narrow inline input shows
// icons as `\uXXXX`; the builder's own wide field shows the real glyph, the
// way the Properties panel's builder always has. Both show `{{value}}`
// rather than the full Jinja expression.

/** @private — model → whatever the user is looking at. */
function toEditable(value, { iconsAsNotation }) {
  const aliased = toIncomingValueToken(value, props.valueExpression);
  return iconsAsNotation ? iconCharsToNotation(aliased) : aliased;
}

/** @private — whatever the user typed/composed → model. */
function toStored(value) {
  return fromIncomingValueToken(iconNotationToChars(value), props.valueExpression);
}

const displayValue = computed(() => toEditable(props.modelValue, { iconsAsNotation: true }));
const builderText  = computed(() => toEditable(props.modelValue, { iconsAsNotation: false }));

function onTyped(value) {
  emit('update:modelValue', toStored(value));
}

function onBuilderApply(text) {
  emit('update:modelValue', toStored(text));
  builderOpen.value = false;
}
</script>
